import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "@apollo/client/react";
import { useNavigate } from "react-router";
import { Dialog } from "@/components/common/Dialog";
import { Button } from "@/components/design/Button";
import { Select } from "@/components/design/Select";
import {
  MyPersonGroupsDocument,
  MyPinGroupsDocument,
  PinType,
} from "@/graphql/generated/operations";
import { usePin } from "@/pages/save/use-pin";
import { useContact } from "@/pages/save/use-contact";
import type { FieldOption } from "@/lib/field-util";
import { logApplicationFailure } from "@/lib/user-error";

// Adds a person to either the user's Pins or their Community (contacts),
// optionally into a specific group ("Ohne Gruppe" = collection without a
// group). Used from the person detail page (Visitenkarte).

type Target = "pins" | "community";
type Status = "idle" | "saving" | "done" | "error";

interface AddToCollectionDialogProps {
  /** Testanker vom Aufrufer — der Dialog wird mehrfach eingehängt (vor/nach dem
   *  Import), eine feste Kennung gäbe es also doppelt. */
  id?: string;
  open: boolean;
  onClose: () => void;
  /** The person(s) to add. Single-element for the profile card; a batch for roster import. */
  contactIds: string[];
  personName?: string;
  /** Which collection is preselected when the dialog opens. */
  target?: Target;
  /**
   * Defer mode: when provided, confirm reports the chosen group ids to the caller
   * INSTEAD of adding (used when the persons don't exist yet — pre-import roster
   * selection). When absent, confirm adds immediately (profile card, post-import).
   */
  onConfirmGroups?: (groupIds: string[]) => void;
}

export function AddToCollectionDialog({
  open,
  onClose,
  contactIds,
  personName,
  target = "pins",
  onConfirmGroups,
  id,
}: AddToCollectionDialogProps) {
  // The collection is fixed by the opener's button (+ Pins / + Community);
  // the parent remounts via key so this state is fresh on each open.
  const [groupId, setGroupId] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const navigate = useNavigate();
  const intl = useIntl();

  // Person-typed pin groups; person/community groups for contacts.
  const { data: pinGroupsData } = useQuery(MyPinGroupsDocument, {
    variables: { input: { pinTypes: [PinType.Person] } },
    skip: !open,
  });
  const { data: personGroupsData } = useQuery(MyPersonGroupsDocument, {
    variables: { input: {} },
    skip: !open,
  });

  const pin = usePin();
  const contact = useContact();

  const groupOptions: FieldOption[] = useMemo(() => {
    if (target === "pins") {
      const groups =
        pinGroupsData?.pinGroups.__typename === "PinGroupResult"
          ? pinGroupsData.pinGroups.pinGroups
          : [];
      return groups.map((g) => ({ value: g.id, label: g.name }));
    }
    const groups =
      personGroupsData?.personGroups.__typename === "PersonGroupResult"
        ? personGroupsData.personGroups.personGroups
        : [];
    return groups.map((g) => ({ value: g.id, label: g.name }));
  }, [target, pinGroupsData, personGroupsData]);

  async function confirm() {
    if (status === "saving") return;
    const groups = groupId ? [groupId] : [];
    // Defer mode: hand the choice back; the caller applies it later (persons don't exist yet).
    if (onConfirmGroups) {
      onConfirmGroups(groups);
      return;
    }
    setStatus("saving");
    try {
      if (target === "pins") {
        await pin.add({ personIds: contactIds, groups });
      } else {
        await contact.add({ contactIds, groups });
      }
      setStatus("done");
    } catch {
      logApplicationFailure("save.add-to-collection");
      setStatus("error");
    }
  }

  const targetLabel = target === "pins" ? "Pins" : "Community";
  const targetPath = target === "pins" ? "/save/pinboard" : "/save/contact";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dismiss="light"
      preventClose={status === "saving"}
      title={intl.formatMessage(
        {
          id: "save.addToCollection.title",
          defaultMessage: "Add {name} to {target}",
        },
        { name: personName ?? intl.formatMessage({ id: "save.addToCollection.personFallback", defaultMessage: "Person" }), target: targetLabel },
      )}
      id={id}
      data-testid="add-to-collection-dialog"
    >
      {status === "done" ? (
        <div className="space-y-5">
          <p className="text-smycles-body text-ink">
            {groupId ? (
              <FormattedMessage
                id="save.addToCollection.doneInGroup"
                defaultMessage="<nm>{name}</nm> was added to <b>{target}</b> in <b>{group}</b>."
                values={{
                  name:
                    personName ??
                    intl.formatMessage({ id: "save.addToCollection.personFallback", defaultMessage: "Person" }),
                  target: targetLabel,
                  group: groupOptions.find((o) => o.value === groupId)?.label,
                  b: (chunks) => <span className="font-medium">{Array.isArray(chunks) ? chunks.map((c, i) => <span key={i}>{c}</span>) : chunks}</span>,
                  nm: (chunks) =>
                    personName ? <span className="font-medium">{Array.isArray(chunks) ? chunks.map((c, i) => <span key={i}>{c}</span>) : chunks}</span> : <>{Array.isArray(chunks) ? chunks.map((c, i) => <span key={i}>{c}</span>) : chunks}</>,
                }}
              />
            ) : (
              <FormattedMessage
                id="save.addToCollection.doneNoGroup"
                defaultMessage="<nm>{name}</nm> was added to <b>{target}</b> (without group)."
                values={{
                  name:
                    personName ??
                    intl.formatMessage({ id: "save.addToCollection.personFallback", defaultMessage: "Person" }),
                  target: targetLabel,
                  b: (chunks) => <span className="font-medium">{Array.isArray(chunks) ? chunks.map((c, i) => <span key={i}>{c}</span>) : chunks}</span>,
                  nm: (chunks) =>
                    personName ? <span className="font-medium">{Array.isArray(chunks) ? chunks.map((c, i) => <span key={i}>{c}</span>) : chunks}</span> : <>{Array.isArray(chunks) ? chunks.map((c, i) => <span key={i}>{c}</span>) : chunks}</>,
                }}
              />
            )}
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="primary"
              onClick={() => {
                onClose();
                navigate(targetPath);
              }}
              className="px-5 py-2"
              data-testid="add-to-collection-go-to-target"
            >
              <FormattedMessage
                id="save.addToCollection.goToTarget"
                defaultMessage="To {target}"
                values={{ target: targetLabel }}
              />
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <div className="smycles-field-label mb-1 text-ink-muted">
              <FormattedMessage id="save.addToCollection.groupLabel" defaultMessage="Group" />
            </div>
            <Select
              id={id && `${id}-group`}
              value={groupId}
              onChange={setGroupId}
              options={groupOptions}
              placeholder={intl.formatMessage({
                id: "save.addToCollection.noGroupPlaceholder",
                defaultMessage: "Without group",
              })}
            />
          </div>

          {status === "error" ? (
            <p className="text-smycles-body text-ink-accent">
              <FormattedMessage
                id="save.addToCollection.error"
                defaultMessage="Could not add. Please try again."
              />
            </p>
          ) : null}

          <div className="flex justify-end gap-3">
            <Button
              variant="primary"
              onClick={() => void confirm()}
              disabled={status === "saving"}
              className={`px-5 py-2 ${status === "saving" ? "cursor-not-allowed opacity-50" : ""}`}
              id={id && `${id}-confirm`}
              data-testid="add-to-collection-confirm"
            >
              {status === "saving" ? (
                <FormattedMessage id="save.addToCollection.adding" defaultMessage="Adding…" />
              ) : (
                `+ ${targetLabel}`
              )}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
